import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowUpRight } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

interface PaywallOverlayProps {
  /** Heading shown on the glass card. */
  title: string;
  /** Body copy explaining what the user unlocks. */
  description: string;
  /** Optional bullet list of perks. */
  perks?: string[];
  /** CTA label. Falls back to the localised `paywall.cta.viewPlans`. */
  ctaLabel?: string;
  /** Where the CTA navigates. Defaults to `/pricing`. */
  ctaTo?: string;
  /** Sample/mock content rendered behind the glass to hint at the feature. */
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a sample/preview of a paid feature with a blurred backdrop and a
 * glass-morphism card explaining the feature must be purchased. Mirrors the
 * "locked game" affordance used in BrandStudio but covers the entire surface
 * so the user gets a clear glimpse of what they're unlocking.
 *
 * The children are made non-interactive (pointer-events: none) and visually
 * dimmed/blurred so the mock data reads as a tease, not as real state.
 */
export function PaywallOverlay({
  title,
  description,
  perks,
  ctaLabel,
  ctaTo = '/pricing',
  children,
  className,
}: PaywallOverlayProps) {
  const navigate = useNavigate();
  const t = useT();
  const resolvedCta = ctaLabel ?? t('paywall.cta.viewPlans');

  return (
    <div className={cn('relative isolate', className)}>
      {/* Mock content — blurred + non-interactive so it reads as a preview. */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none opacity-60"
        style={{ filter: 'blur(6px) saturate(0.85)' }}
      >
        {children}
      </div>

      {/* Glass card centered over the mock. */}
      <div className="absolute inset-0 z-10 flex items-center justify-center px-4 py-8">
        <div
          className="w-full max-w-md rounded-2xl border border-white/10 p-6 text-center shadow-2xl"
          style={{
            // Glass / crystal effect: semi-transparent surface + heavy backdrop
            // blur. The accent-tinted gradient hints at the Xotski brand without
            // overwhelming the message.
            background:
              'linear-gradient(135deg, rgba(178, 98, 245, 0.18) 0%, rgba(20, 18, 32, 0.55) 60%, rgba(20, 18, 32, 0.65) 100%)',
            backdropFilter: 'blur(18px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(18px) saturate(1.4)',
            boxShadow:
              '0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <div className="mb-3 flex items-center justify-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest"
              style={{
                fontFamily: 'var(--font-display)',
                color: 'var(--color-xk-accent)',
                background: 'rgba(178, 98, 245, 0.15)',
                border: '1px solid rgba(178, 98, 245, 0.4)',
              }}
            >
              <Sparkles className="h-3 w-3" />
              {t('paywall.badge.premium')}
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/70"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <Lock className="h-3 w-3" />
              {t('paywall.badge.locked')}
            </span>
          </div>

          <h2
            className="mb-2 text-xl font-black tracking-tight text-white"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {title}
          </h2>
          <p className="mb-5 text-sm leading-relaxed text-white/80">
            {description}
          </p>

          {perks && perks.length > 0 && (
            <ul className="mb-5 space-y-1.5 text-left text-[12px] text-white/75">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1 inline-block h-1.5 w-1.5 flex-none rounded-full"
                    style={{ background: 'var(--color-xk-accent)' }}
                  />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}

          <Button
            onClick={() => navigate(ctaTo)}
            rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}
            className="w-full"
          >
            {resolvedCta}
          </Button>
        </div>
      </div>
    </div>
  );
}
